import React, { useState, useEffect } from 'react';
import WishlistTooltip from './WishlistTooltip';
import { initWishlist } from '../../../../../snippets/customer.wishlist';
import { dataLayerPushEventAction, messageConstants } from '../../utils/utils';
import { isFirefoxStrictModeEnabled } from '@jbhi-fi/utils/src/firefoxStrictMode';
import { useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { trackAddToWishlist, trackRemoveFromWishlist } from '@jbhi-fi/utils/src/ga4Tracking';
import { transformShopifyProductForTracking } from '../../../../../utils/cart/transformShopifyProductForTracking';
import { getStoreCurrency } from '../../../../../utils/shopify/getStoreCurrency';
import { WishlistActionType, useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { WishlistButton } from '@jbhi-fi/components/src/WishlistButton/WishlistButton';
import { styles } from './WishlistButtonPDP.css';

export type WishlistButtonPDPProps = {
  productCartData?: any;
  sku: number;
  product?: any;
  className?: string;
  onlineValue?: any;
  gtmData?: any;
  buttonType?: string;
  variantAvailable?: boolean;
  lrpStockAvailable?: boolean;
  lrpExists?: boolean;
  isDigital?: boolean;
  priceTagWidth?: number;
  variantSku?: string;
  shouldShowCompare?: boolean;
};

export const WishlistButtonPDP = ({
  productCartData,
  sku,
  product,
  gtmData,
  buttonType,
  variantAvailable,
  isDigital,
  onlineValue,
  priceTagWidth,
  variantSku,
  shouldShowCompare,
}: WishlistButtonPDPProps): JSX.Element => {
  const noPhysicalStock = buttonType === 'DoNotDisplay' || (!variantAvailable && buttonType !== 'NotifyMe');
  const isDesktop = useBreakpoint('md', 'up');
  const shouldShowText =
    (noPhysicalStock && buttonType !== 'CheckAvailability') ||
    shouldShowCompare ||
    !isDesktop ||
    (buttonType === 'NotifyMe' && isFirefoxStrictModeEnabled());

  const { itemsInWishlist, handleUpdateWishlist, getIsInWishlist, isWishlistLoading, isLoggedIn } =
    useCustomerContext();

  const variantId: number = product?.variants[0]?.id;
  const skuString = sku.toString();

  const [fullWishlist, setFullWishlist] = useState<boolean>(false);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [displayLoginModal, setDisplayLoginModal] = useState<boolean>(false);
  const [wishlistIconState, setWishlistIconState] = useState<string>('toAdd');
  const [wishlistActionType, setWishlistActionType] = useState<WishlistActionType>('addItem');
  const [tooltipMessage, setTooltipMessage] = useState<string>('');

  const removeItemMessage = messageConstants.removeItemMessage;
  const isFullMessage = messageConstants.isFullMessage;
  const loginMessage = messageConstants.loginMessage;
  const addedItemMessage = messageConstants.addedItemMessage;
  const removedItemMessage = messageConstants.removedItemMessage;
  initWishlist();

  // Check if item is in wishlist upon each variant change
  useEffect((): void => {
    if (!itemsInWishlist) return;
    if (itemsInWishlist.length > 99) {
      setFullWishlist(true);
      return;
    } else if (getIsInWishlist(`${sku}`)) {
      setWishlistIconState('added');
      setWishlistActionType('removeItem');
      setTooltipMessage(removeItemMessage);
    } else {
      setWishlistIconState('toAdd');
      setWishlistActionType('addItem');
    }
  }, [variantSku]);

  // Click handler for Wishlist Icon
  const wishlistClickHandler = async (): Promise<void> => {
    if (!isLoggedIn) {
      setTooltipVisible(!tooltipVisible);
      setDisplayLoginModal(true);
      setTooltipMessage(loginMessage);
      dataLayerPushEventAction('wishlist', 'attempt', gtmData);
    } else if (isLoggedIn && fullWishlist) {
      if (displayLoginModal) setDisplayLoginModal(false);
      setTooltipMessage(isFullMessage);
    } else {
      const wishlistTrackingProps = {
        currency: getStoreCurrency(),
        item: transformShopifyProductForTracking({ item: onlineValue, listName: 'pdp' }),
      };
      wishlistActionType === 'addItem'
        ? trackAddToWishlist(wishlistTrackingProps)
        : trackRemoveFromWishlist(wishlistTrackingProps);
      try {
        await handleUpdateWishlist({
          actionType: wishlistActionType,
          sku: skuString,
          variantId,
        });
        if (wishlistActionType === 'addItem') {
          setWishlistActionType('removeItem');
          setWishlistIconState('added');
          setTooltipVisible(true);
          setTooltipMessage(addedItemMessage);
          setTimeout(() => setTooltipVisible(false), 3000);
          dataLayerPushEventAction('wishlist', 'add', gtmData);
        } else {
          setWishlistActionType('addItem');
          setWishlistIconState('toAdd');
          setTooltipVisible(true);
          setTooltipMessage(removedItemMessage);
          setTimeout(() => setTooltipVisible(false), 3000);
          dataLayerPushEventAction('wishlist', 'remove', gtmData);
        }
      } catch (error) {
        console.error('Error adding or removing item from wishlist', error);
      }
    }
  };

  return (
    <div className={shouldShowText ? styles.fullWidth : ''} id={useIdPrefix('wishlist-cta-container')}>
      <div id={useIdPrefix('wishlist-cta')} className={styles.wishlistContainer({ shouldShowText })}>
        <WishlistTooltip
          productCartData={productCartData}
          sku={sku}
          isDigital={isDigital}
          priceTagWidth={priceTagWidth}
          tooltipVisible={tooltipVisible}
          displayLoginModal={displayLoginModal}
          tooltipMessage={tooltipMessage}
          variantId={variantId}
          isLoggedIn={isLoggedIn}
          setToolTipVisible={setTooltipVisible}
          setDisplayLoginModal={setDisplayLoginModal}
        />
        <WishlistButton
          testId="button-wishlist"
          isLoading={isWishlistLoading ?? false}
          state={wishlistIconState === 'added' ? 'added' : 'default'}
          type="secondary"
          onClick={wishlistClickHandler}
          fullWidth={shouldShowText}
          useShortLabel={shouldShowCompare}
        />
      </div>
    </div>
  );
};

export default WishlistButtonPDP;
